import { ReactNode } from "react";
import { LeadingActions, TrailingActions } from "react-swipeable-list";
import { ActionGenerator } from "../utils/actionGenerator";
import { useApiMutation } from "../../../infrastructure/hooks/useApiMutation";
import { useItemsListPermissions } from "./useItemsListPermissions";
import { ISwipableAction } from "../types/ItemsList.types";

export const useItemsListActions = <TItem extends { [key: string]: any }>(
  onEdit: (id: number) => void,
  swipeLeftActions?: ISwipableAction<TItem>[],
  swipeRightActions?: ISwipableAction<TItem>[],
  iconHeight: string = "30px",
  iconWidth: string = "30px"
) => {
  const { mutate } = useApiMutation();
  const { filterActionsByPermissions } = useItemsListPermissions<TItem>();
  const actionGenerator = new ActionGenerator<TItem>(mutate);

  const generateMobileActions = (
    item: TItem,
    actions?: ISwipableAction<TItem>[]
  ): ReactNode => {
    if (!actions?.length) return undefined;

    const allowedActions = filterActionsByPermissions(actions, item);
    if (!allowedActions.length) return undefined;

    return actionGenerator.generateActions(
      item,
      allowedActions,
      onEdit,
      "mobile"
    );
  };

  const generateDesktopActions = (
    item: TItem,
    leftActions?: ISwipableAction<TItem>[],
    rightActions?: ISwipableAction<TItem>[]
  ): ReactNode => {
    const allowedLeftActions = filterActionsByPermissions(leftActions, item);
    const allowedRightActions = filterActionsByPermissions(rightActions, item);

    const allActions = [...allowedLeftActions, ...allowedRightActions];

    if (!allActions.length) return undefined;

    const actionsElement = actionGenerator.generateActions(
      item,
      allActions,
      onEdit,
      "desktop",
      iconHeight,
      iconWidth
    );

    if (!actionsElement) return undefined;

    return (
      <div style={{ display: "flex", gap: "10px", marginRight: "10px" }}>
        {actionsElement}
      </div>
    );
  };

  // Mobile swipe actions
  const leadingActions = (item: TItem) => {
    const actions = generateMobileActions(item, swipeLeftActions);
    return actions ? <LeadingActions>{actions}</LeadingActions> : undefined;
  };

  const trailingActions = (item: TItem) => {
    const actions = generateMobileActions(item, swipeRightActions);
    return actions ? <TrailingActions>{actions}</TrailingActions> : undefined;
  };

  // Desktop action buttons
  const actionButtons = (item: TItem) => {
    return generateDesktopActions(item, swipeLeftActions, swipeRightActions);
  };

  return {
    leadingActions,
    trailingActions,
    actionButtons,
  };
};
